import React from 'react';
import { withRouter, Link } from "react-router-dom";
import moment from 'moment';
import { makeStyles, Typography, Tooltip, Icon, Box, Chip, Paper } from '@material-ui/core';
import BoothIdLink from './BoothIdLink';
import LocationDisplay from './LocationDisplay';

/**
 * Reusable component for displaying order information in a card format
 *
 * @param {Object} props Component props
 * @param {Object} props.order The order object to display
 * @param {string} [props.size="default"] Size of the card: "small", "default", or "large"
 * @param {string|number} [props.width="auto"] Width of the card: "auto", "full", or specific pixel/percentage value
 */
function OrderCard({ order, size = 'default', width = 'auto', ...otherProps }) {
  const cls = useStyles();

  // Determine order status properties
  const orderStatus = order.status;
  const now = moment();
  const eventDate = moment(order.event_date);
  const shipDate = moment(order.ship_date);
  const returnDate = moment(order.return_date);
  const isLateToReturn = orderStatus === 'Packing' && returnDate.isValid() && returnDate.isBefore(now, 'day');
  const isPastShipDate = shipDate.isValid() && shipDate.isBefore(now, 'day');
  const daysUntilShip = Math.max(0, shipDate.diff(now, 'days'));
  const isUrgent = daysUntilShip <= 1 && !['Outbound', 'Delivered'].includes(orderStatus);

  // Determine status classes and properties
  let statusClass = cls.statusNew;
  let statusIcon = 'error_outline';
  let statusText = 'Needs Prep';
  let statusBadgeClass = cls.statusBadgeNew;

  // Comprehensive status determination
  switch (orderStatus) {
    case 'New':
      statusClass = cls.statusNew;
      statusIcon = 'fiber_new';
      statusText = 'Needs Prep';
      statusBadgeClass = cls.statusBadgeNew;

      // Check if past ship date
      if (isPastShipDate) {
        statusClass = cls.statusLate;
        statusIcon = 'warning';
        statusText = 'Late Prep';
        statusBadgeClass = cls.statusBadgeLate;
      }
      break;

    case 'Ready':
      statusClass = cls.statusReady;
      statusIcon = 'local_shipping';
      statusText = 'Ready to Outbound';
      statusBadgeClass = cls.statusBadgeReady;

      // Check if past ship date
      if (isPastShipDate) {
        statusClass = cls.statusLate;
        statusIcon = 'warning';
        statusText = 'Ready (Late)';
        statusBadgeClass = cls.statusBadgeLate;
      }
      break;

    case 'Outbound':
      // Check if has been dropped off
      if (order.outbound_shipment_activity && order.outbound_shipment_activity.shipped === true) {
        statusClass = cls.statusShipped;
        statusIcon = 'local_shipping';
        statusText = 'In Transit';
        statusBadgeClass = cls.statusBadgeShipped;
      } else {
        statusClass = cls.statusReady;
        statusIcon = 'markunread_mailbox';
        statusText = 'Ready for Drop-off';
        statusBadgeClass = cls.statusBadgeReady;

        // Check if past ship date
        if (isPastShipDate) {
          statusClass = cls.statusLate;
          statusIcon = 'warning';
          statusText = 'Drop-off Overdue';
          statusBadgeClass = cls.statusBadgeLate;
        }
      }
      break;

    case 'Delivered':
      statusClass = cls.statusDelivered;
      statusIcon = 'check_circle';
      statusText = 'Delivered';
      statusBadgeClass = cls.statusBadgeDelivered;
      break;

    case 'Packing':
      statusClass = cls.statusPacking;
      statusIcon = 'inventory_2';
      statusText = 'Packing';
      statusBadgeClass = cls.statusBadgePacking;

      // Check if late to return
      if (isLateToReturn) {
        statusClass = cls.statusLate;
        statusIcon = 'warning';
        statusText = 'Return Overdue';
        statusBadgeClass = cls.statusBadgeLate;
      }
      break;

    case 'Inbound':
      statusClass = cls.statusInbound;
      statusIcon = 'assignment_return';
      statusText = 'Returning';
      statusBadgeClass = cls.statusBadgeInbound;
      break;

    case 'Returned':
      statusClass = cls.statusReturned;
      statusIcon = 'inventory';
      statusText = 'Needs Check-in';
      statusBadgeClass = cls.statusBadgeReturned;
      break;

    case 'Done':
      statusClass = cls.statusDone;
      statusIcon = 'task_alt';
      statusText = 'Completed';
      statusBadgeClass = cls.statusBadgeDone;
      break;

    case 'Canceled':
      statusClass = cls.statusCanceled;
      statusIcon = 'cancel';
      statusText = 'Canceled';
      statusBadgeClass = cls.statusBadgeCanceled;
      break;

    default:
      // Default already set
      break;
  }

  // Set card width based on the width prop
  const cardStyle = {};
  if (width === 'full') {
    cardStyle.width = '100%';
  } else if (width !== 'auto') {
    cardStyle.width = width;
  }

  return (
    <Link to={`/orders/${order.id}`} className={cls.cardLink} style={cardStyle}>
      <Paper
        className={`${cls.card} ${['Outbound', 'Delivered'].includes(orderStatus) ? cls.shippedCard : ''} ${
          size === 'small' ? cls.smallCard : ''
        }`}
        elevation={isUrgent ? 3 : 1}
        style={cardStyle}
      >
        {/* Status Border */}
        <div className={`${cls.statusBorder} ${statusClass}`} />

        {/* Card Content */}
        <div className={cls.cardContent}>
          {/* Header with Customer Name and Order ID */}
          <div className={cls.cardHeader}>
            <div className={cls.customerContainer}>
              <Icon fontSize='small' className={cls.customerIcon}>
                person
              </Icon>
              <Typography variant='subtitle1' className={cls.customerText} noWrap>
                {order.event_name || 'No Customer Name'}
              </Typography>
            </div>
            <Typography variant='caption' className={cls.orderId}>
              #{order.id}
            </Typography>
          </div>

          {/* Event name as secondary info */}
          <Typography variant='body2' className={cls.eventName} noWrap>
            {order.event_name}
          </Typography>

          {/* The content below will only be shown for 'default' and 'large' sizes */}
          {size !== 'small' && (
            <>
              {/* Transit Information */}
              <div className={cls.infoRow}>
                <div className={cls.infoItem}>
                  <Icon fontSize='small' className={cls.infoIcon}>
                    schedule
                  </Icon>
                  <Typography variant='body2' className={cls.infoText}>
                    {order.transit_days} days transit
                  </Typography>
                </div>
              </div>

              {/* Dates */}
              <div className={cls.datesRow}>
                <div className={cls.dateItem}>
                  <Typography variant='caption' className={cls.dateLabel}>
                    Ship:
                  </Typography>
                  <Typography variant='body2' className={cls.dateValue}>
                    {shipDate.format('MMM D')}
                  </Typography>
                </div>
                <Icon fontSize='small' className={cls.dateArrow}>
                  arrow_forward
                </Icon>
                <div className={cls.dateItem}>
                  <Typography variant='caption' className={cls.dateLabel}>
                    Event:
                  </Typography>
                  <Typography variant='body2' className={cls.dateValue}>
                    {eventDate.format('MMM D')}
                  </Typography>
                </div>
              </div>

              {/* Props */}
              <div className={cls.propsRow}>
                <Typography variant='caption' className={cls.propsLabel}>
                  Props:
                </Typography>
                <Typography variant='body2' className={cls.propsValue} noWrap>
                  {order.props || 'None'}
                </Typography>
              </div>
            </>
          )}

          {/* Order attributes - shown in small size */}
          <div className={`${cls.attributesRow} ${size === 'small' ? cls.smallAttributesRow : ''}`}>
            {/* Booth ID is important so always show it */}
            {order.booth_id && (
              <div className={cls.boothIdContainer}>
                <BoothIdLink boothId={order.booth_id} chip={false} size='small' />
              </div>
            )}

            {/* Location is important so always show it */}
            {order.shipping_city && order.shipping_state && (
              <div className={cls.locationContainer}>
                <LocationDisplay city={order.shipping_city} state={order.shipping_state} chip={false} size='small' />
              </div>
            )}

            {/* For small size, only show date badges */}
            {size === 'small' ? (
              <>
                <Tooltip title={`Ship date: ${shipDate.format('MMM D')}`}>
                  <div className={cls.iconBadge}>
                    <Icon fontSize='small'>local_shipping</Icon>
                    {shipDate.format('MM/DD')}
                  </div>
                </Tooltip>
                <Tooltip title={`Event date: ${eventDate.format('MMM D')}`}>
                  <div className={cls.iconBadge}>
                    <Icon fontSize='small'>event</Icon>
                    {eventDate.format('MM/DD')}
                  </div>
                </Tooltip>
                {isUrgent && <Chip size='small' className={cls.urgentChip} label='URGENT' color='secondary' />}
              </>
            ) : (
              <>
                <Tooltip title={order.print_count === 0 ? 'Digital order (no printer)' : `${order.print_count} prints`}>
                  <div className={`${cls.iconBadge} ${order.print_count === 0 ? cls.digitalBadge : ''}`}>
                    <Icon fontSize='small'>{order.print_count === 0 ? 'phone_iphone' : 'print'}</Icon>
                    {order.print_count === 0 ? 'Digital' : order.print_count}
                  </div>
                </Tooltip>
                <Tooltip title={order.custom_logo ? 'Custom logo' : 'Default logo'}>
                  <div className={cls.iconBadge}>
                    <Icon fontSize='small'>filter_vintage</Icon>
                    {order.custom_logo ? 'Custom' : 'Default'}
                  </div>
                </Tooltip>
                <Tooltip title={`${order.backdrop_color} backdrop`}>
                  <div className={cls.iconBadge}>
                    <Icon fontSize='small'>bookmark_border</Icon>
                    {order.backdrop_color}
                  </div>
                </Tooltip>
                {isUrgent && !['Outbound', 'Delivered'].includes(orderStatus) && (
                  <Chip size='small' className={cls.urgentChip} label='URGENT' color='secondary' />
                )}
              </>
            )}
          </div>

          {/* Status Footer */}
          <div className={`${cls.statusFooter} ${statusClass}`}>
            <div className={`${cls.statusBadge} ${statusBadgeClass}`}>
              <Icon fontSize='inherit' style={{ marginRight: '4px', fontSize: '0.8rem' }}>
                {statusIcon}
              </Icon>
              <span>{statusText}</span>
            </div>
          </div>
        </div>
      </Paper>
    </Link>
  );
}

const useStyles = makeStyles(theme => ({
  cardLink: {
    textDecoration: 'none',
    display: 'block',
    transition: 'transform 0.2s ease',
    '&:hover': {
      transform: 'translateY(-3px)',
    },
  },
  card: {
    position: 'relative',
    overflow: 'hidden',
    borderRadius: theme.shape.borderRadius,
    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
    transition: 'box-shadow 0.2s ease',
    '&:hover': {
      boxShadow: '0 3px 8px rgba(0,0,0,0.15)',
    },
    paddingLeft: '8px', // Make room for status border
  },
  smallCard: {
    minHeight: '100px',
  },
  shippedCard: {
    backgroundColor: 'rgba(0,0,0,0.02)',
    opacity: 0.8,
  },
  urgentCard: {
    // Remove the border-left style since it's redundant with the status border
    // borderLeft: `4px solid ${theme.palette.error.main}`,
    // paddingLeft: '4px', // Adjust for the urgent border
  },
  statusBorder: {
    position: 'absolute',
    top: 0,
    left: 0,
    bottom: 0,
    width: '8px',
  },
  statusNew: {
    backgroundColor: theme.palette.warning.main,
  },
  statusReady: {
    backgroundColor: theme.palette.info.main,
  },
  statusShipped: {
    backgroundColor: theme.palette.success.main,
  },
  statusDelivered: {
    backgroundColor: '#4CAF50', // Green
  },
  statusPacking: {
    backgroundColor: '#9C27B0', // Purple
  },
  statusInbound: {
    backgroundColor: '#FF9800', // Orange
  },
  statusReturned: {
    backgroundColor: '#607D8B', // Blue Grey
  },
  statusDone: {
    backgroundColor: '#78909C', // Light Blue Grey
  },
  statusCanceled: {
    backgroundColor: '#9E9E9E', // Grey
  },
  statusLate: {
    backgroundColor: theme.palette.error.main, // Red for late items
  },
  cardContent: {
    padding: theme.spacing(1.5),
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(0.5),
  },
  customerContainer: {
    display: 'flex',
    alignItems: 'center',
    flexGrow: 1,
    marginRight: theme.spacing(1),
  },
  customerIcon: {
    color: theme.palette.primary.main,
    marginRight: theme.spacing(0.5),
    fontSize: '1rem',
  },
  customerText: {
    fontWeight: 600,
    fontSize: '1rem',
    color: theme.palette.primary.dark,
  },
  eventName: {
    fontSize: '0.85rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  orderId: {
    color: theme.palette.text.secondary,
    fontWeight: 400,
  },
  infoRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  infoItem: {
    display: 'flex',
    alignItems: 'center',
  },
  infoIcon: {
    color: theme.palette.text.secondary,
    marginRight: theme.spacing(0.5),
    fontSize: '0.9rem',
  },
  infoText: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary,
  },
  datesRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
    padding: theme.spacing(0.5, 0),
    backgroundColor: 'rgba(0,0,0,0.03)',
    borderRadius: theme.shape.borderRadius,
  },
  dateItem: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(0, 1),
  },
  dateLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
  },
  dateValue: {
    fontSize: '0.85rem',
    fontWeight: 500,
  },
  dateArrow: {
    color: theme.palette.text.disabled,
    fontSize: '0.8rem',
  },
  propsRow: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  propsLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.7rem',
    marginRight: theme.spacing(0.5),
  },
  propsValue: {
    fontSize: '0.8rem',
  },
  attributesRow: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.75),
    marginBottom: theme.spacing(1.5),
  },
  smallAttributesRow: {
    marginTop: theme.spacing(1),
  },
  iconBadge: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.7rem',
    backgroundColor: 'rgba(0,0,0,0.05)',
    padding: '2px 6px',
    borderRadius: '4px',
    '& .MuiIcon-root': {
      fontSize: '0.9rem',
      marginRight: '4px',
    },
  },
  statusFooter: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(1),
    marginLeft: theme.spacing(-1.5),
    marginRight: theme.spacing(-1.5),
    marginBottom: theme.spacing(-1.5),
    borderTop: '1px solid rgba(0,0,0,0.05)',
    padding: theme.spacing(0.75, 1.5),
  },
  statusBadge: {
    borderRadius: '4px',
    padding: '2px 6px',
    fontSize: '0.7rem',
    fontWeight: 'bold',
    display: 'inline-flex',
    alignItems: 'center',
  },
  statusBadgeNew: {
    backgroundColor: theme.palette.warning.light,
    color: theme.palette.warning.contrastText,
  },
  statusBadgeReady: {
    backgroundColor: theme.palette.info.light,
    color: theme.palette.info.contrastText,
  },
  statusBadgeShipped: {
    backgroundColor: theme.palette.success.light,
    color: theme.palette.success.contrastText,
  },
  statusBadgeDelivered: {
    backgroundColor: 'rgba(76, 175, 80, 0.15)', // Slightly darker green background
    color: theme.palette.success.dark,
  },
  statusBadgePacking: {
    backgroundColor: 'rgba(156, 39, 176, 0.15)', // Slightly darker purple background
    color: '#6A1B9A', // Darker purple for better contrast
  },
  statusBadgeInbound: {
    backgroundColor: 'rgba(255, 152, 0, 0.15)', // Slightly darker orange background
    color: '#E65100', // Darker orange for better contrast
  },
  statusBadgeReturned: {
    backgroundColor: 'rgba(96, 125, 139, 0.15)', // Slightly darker blue grey background
    color: '#37474F', // Darker blue grey for better contrast
  },
  statusBadgeDone: {
    backgroundColor: 'rgba(120, 144, 156, 0.15)', // Slightly darker blue grey background
    color: '#37474F', // Darker blue grey for better contrast
  },
  statusBadgeCanceled: {
    backgroundColor: 'rgba(158, 158, 158, 0.15)', // Slightly darker grey background
    color: '#424242', // Darker grey for better contrast
  },
  statusBadgeLate: {
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
  },
  urgentChip: {
    height: 20,
    fontSize: '0.65rem',
    fontWeight: 600,
  },
  boothIdContainer: {
    marginRight: theme.spacing(0.5),
  },
  locationContainer: {
    marginRight: theme.spacing(0.5),
  },
  digitalBadge: {
    backgroundColor: 'rgba(33, 150, 243, 0.12)',
    color: '#1565C0',
    fontWeight: 500,
  },
}));

export default withRouter(OrderCard);